import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ActionItem } from '../types';
import { useAuthStore } from '../store/authStore';
import { useToast } from '../contexts/ToastContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { usePageTitle } from '../hooks/usePageTitle';
import Select from '../components/Select';

type Priority = 'low' | 'medium' | 'high' | 'urgent';
type Status = 'todo' | 'in_progress' | 'done' | 'cancelled';

const priorityConfig: Record<Priority, { label: string; color: string; bgColor: string }> = {
  low: { label: 'Low', color: 'text-slate-600', bgColor: 'bg-slate-100' },
  medium: { label: 'Medium', color: 'text-orange-600', bgColor: 'bg-orange-50' },
  high: { label: 'High', color: 'text-orange-600', bgColor: 'bg-orange-50' },
  urgent: { label: 'Urgent', color: 'text-red-600', bgColor: 'bg-red-50' },
};

const statusConfig: Record<Status, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  todo: { 
    label: 'To Do', 
    color: 'text-slate-600', 
    bgColor: 'bg-slate-100',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth={2} /></svg>
  },
  in_progress: { 
    label: 'In Progress', 
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  },
  done: { 
    label: 'Done', 
    color: 'text-emerald-600', 
    bgColor: 'bg-emerald-50',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  },
  cancelled: { 
    label: 'Cancelled', 
    color: 'text-red-600', 
    bgColor: 'bg-red-50',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  },
};

interface BoardInfo {
  id: string;
  title: string;
}

interface ActionItemWithBoard extends ActionItem {
  board_title?: string;
}

const ActionsPage: React.FC = () => {
  usePageTitle('Action Items');
  const [actionItems, setActionItems] = useState<ActionItemWithBoard[]>([]);
  const [, setBoards] = useState<BoardInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Status | 'all'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'priority' | 'status'>('date');
  const { showSuccess, showError } = useToast();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getToken = () => useAuthStore.getState().tokens?.access;

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all boards first
      const boardsRes = await axios.get(`${API_BASE_URL}/boards/`, { headers });
      const boardsList = boardsRes.data.results || boardsRes.data || [];
      setBoards(boardsList);

      // Fetch action items from all boards
      const allActions: ActionItemWithBoard[] = [];
      for (const board of boardsList) {
        try {
          const actionsRes = await axios.get(`${API_BASE_URL}/action-items/board/${board.id}/`, { headers });
          const items = actionsRes.data.action_items || actionsRes.data.results || actionsRes.data || [];
          items.forEach((item: ActionItem) => {
            allActions.push({ ...item, board_title: board.title });
          });
        } catch {
          // Board might not have action items endpoint, skip
        }
      }
      setActionItems(allActions);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateActionStatus = async (id: string, status: Status) => {
    try {
      const token = getToken();
      await axios.patch(
        `${API_BASE_URL}/action-items/${id}/`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActionItems(prev => prev.map(item => 
        item.id === id ? { ...item, status } : item
      ));
      showSuccess('Status updated');
    } catch (error: unknown) {
      console.error('Failed to update action:', error);
      const axiosError = error as { response?: { data?: { detail?: string } } };
      showError(axiosError.response?.data?.detail || 'Failed to update status');
    }
  };

  const deleteAction = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this action item?')) return;
    
    try {
      const token = getToken();
      await axios.delete(`${API_BASE_URL}/action-items/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActionItems(prev => prev.filter(item => item.id !== id));
      showSuccess('Action item deleted');
    } catch (error: unknown) {
      console.error('Failed to delete action:', error);
      const axiosError = error as { response?: { data?: { detail?: string } } };
      showError(axiosError.response?.data?.detail || 'Failed to delete action item');
    }
  };

  const filteredItems = actionItems.filter(item => {
    if (filter === 'all') return true;
    return item.status === filter;
  });

  const sortedItems = [...filteredItems].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
    if (sortBy === 'priority') {
      const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
      return priorityOrder[a.priority as Priority] - priorityOrder[b.priority as Priority];
    }
    if (sortBy === 'status') {
      const statusOrder = { todo: 0, in_progress: 1, done: 2, cancelled: 3 };
      return statusOrder[a.status as Status] - statusOrder[b.status as Status];
    }
    return 0;
  });

  const stats = {
    total: actionItems.length,
    todo: actionItems.filter(i => i.status === 'todo').length,
    inProgress: actionItems.filter(i => i.status === 'in_progress').length,
    done: actionItems.filter(i => i.status === 'done').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">
            Action Items
          </h1>
          <p className="text-slate-500">
            Track and manage tasks from your retrospectives
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Total</p>
            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
          </div>
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">To Do</p>
            <p className="text-2xl font-bold text-slate-900">{stats.todo}</p>
          </div>
          <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
            <p className="text-xs font-medium text-orange-600 uppercase tracking-wide mb-1">In Progress</p>
            <p className="text-2xl font-bold text-orange-600">{stats.inProgress}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
            <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Done</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.done}</p>
          </div>
        </div>

        {/* Filters & Sort */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {(['all', 'todo', 'in_progress', 'done'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  filter === f
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {f === 'all' ? 'All' : statusConfig[f as Status].label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Sort by:</span>
            <Select
              value={sortBy}
              onChange={(v) => setSortBy(v as typeof sortBy)}
              size="sm"
              className="w-28"
              options={[
                { value: 'date', label: 'Date' },
                { value: 'priority', label: 'Priority' },
                { value: 'status', label: 'Status' },
              ]}
            />
          </div>
        </div>

        {/* Action Items List */}
        {sortedItems.length === 0 ? (
          <div className="text-center py-16 bg-slate-50 border border-slate-100 rounded-xl">
            <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-xl flex items-center justify-center">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {filter === 'all' ? 'No action items yet' : `No ${statusConfig[filter as Status]?.label.toLowerCase()} items`}
            </h3>
            <p className="text-slate-500 mb-6">
              Action items will appear here when you create them during retrospectives
            </p>
            <Link
              to="/app/dashboard"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
            >
              Go to Dashboard
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedItems.map((item) => (
              <ActionItemRow
                key={item.id}
                item={item}
                onStatusChange={(status) => updateActionStatus(item.id, status)}
                onDelete={() => deleteAction(item.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface ActionItemRowProps {
  item: ActionItemWithBoard;
  onStatusChange: (status: Status) => void;
  onDelete: () => void;
}

const ActionItemRow: React.FC<ActionItemRowProps> = ({ item, onStatusChange, onDelete }) => {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const status = statusConfig[item.status as Status];
  const priority = priorityConfig[item.priority as Priority];

  const isOverdue = item.due_date && new Date(item.due_date) < new Date() && item.status !== 'done';

  return (
    <div className={`bg-white border rounded-xl p-4 hover:shadow-md transition-all ${
      item.status === 'done' ? 'border-slate-100 opacity-60' : 'border-slate-200'
    }`}>
      <div className="flex items-start gap-4">
        {/* Checkbox */}
        <button
          onClick={() => onStatusChange(item.status === 'done' ? 'todo' : 'done')}
          className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
            item.status === 'done' 
              ? 'bg-emerald-500 border-emerald-500 text-white' 
              : 'border-slate-300 hover:border-emerald-400'
          }`}
        >
          {item.status === 'done' && (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h4 className={`font-medium ${item.status === 'done' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
              {item.title}
            </h4>
            <button
              onClick={onDelete}
              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>

          {item.description && (
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{item.description}</p>
          )}

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {/* Status */}
            <div className="relative">
              <button
                onClick={() => setShowStatusMenu(!showStatusMenu)}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${status.bgColor} ${status.color}`}
              >
                {status.icon}
                {status.label}
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showStatusMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowStatusMenu(false)} />
                  <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border z-20 py-1 min-w-[140px]">
                    {(Object.keys(statusConfig) as Status[]).map((s) => (
                      <button
                        key={s}
                        onClick={() => {
                          onStatusChange(s);
                          setShowStatusMenu(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 ${
                          s === item.status ? 'bg-slate-50' : ''
                        }`}
                      >
                        <span className={statusConfig[s].color}>{statusConfig[s].icon}</span>
                        {statusConfig[s].label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Priority */}
            <span className={`px-2 py-1 rounded-md text-xs font-medium ${priority.bgColor} ${priority.color}`}>
              {priority.label}
            </span>

            {/* Due Date */}
            {item.due_date && (
              <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                isOverdue ? 'bg-red-50 text-red-600' : 'bg-orange-50 text-orange-500'
              }`}>
                {isOverdue ? '⚠️ Overdue: ' : '📅 '}
                {new Date(item.due_date).toLocaleDateString()}
              </span>
            )}

            {/* Board */}
            {item.board_title && (
              <Link
                to={`/app/board/${item.board}`}
                className="px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
              >
                📋 {item.board_title}
              </Link>
            )}

            {/* Assignee */}
            {item.assigned_to && (
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium"
                style={{ backgroundColor: item.assigned_to.avatar_color }}
                title={item.assigned_to.display_name}
              >
                {item.assigned_to.initials}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionsPage;

